import axios from 'axios';

import { md5 } from '~/core/helpers';

class ImageUtilsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageUtilsError';
  }
}

/**
 * Converts a blob image into a base64 DataURL
 * @example
 *  const dataURL = await ImageUtils.getBase64FromBlob(imageBlob);
 *  console.debug(dataURL) // data:image/jpeg;base64,/9j/2wBD...
 */
export const getBase64FromBlob = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new ImageUtilsError('Error occurred reading blob content'));
    reader.readAsDataURL(blob);
  });

/**
 * Fetch an image from an URL, then converts it to a base64 DataURL
 * @example
 *  const dataURL = await ImageUtils.getBase64FromURL('https://www.domain.com/path/to/image.jpg');
 *  console.debug(dataURL) // data:image/jpeg;base64,/9j/2wBD...
 */
export const getBase64FromURL = async (url: string): Promise<string> => {
  try {
    const { data: blob } = await axios.get<Blob>(url, {
      responseType: 'blob',
    });

    return getBase64FromBlob(blob);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error(error.response.data);
        console.error(error.response.status);
        console.error(error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.error(error.request);
      }
    }

    return Promise.reject(error);
  }
};

/**
 * Generate an S3 URL based on a bucket and key for an object
 * @param bucket    the bucket the file is located in
 * @param key       the key for the file
 * @param newStyle  true for the new style url (default)
 * @returns {string}    a generic url to
 */
export const s3URIFromBucketAndKey = (
  bucket: string,
  key: string,
  newStyle = true
) => {
  let uri = null;

  if (newStyle) {
    uri = `https://${bucket}.s3.amazonaws.com/${key}`;
  }
  // this is the deprecated style URL
  else {
    uri = `https://s3.amazonaws.com/${bucket}/${key}`;
  }

  return uri;
};

/**
 * Extract bucket name and file key from s3 url, old or new style
 * @param s3Uri the https://s3.
 * @returns {*[]}
 */
export const bucketAndKeyFromS3URI = (s3Uri: string) => {
  s3Uri = s3Uri.replace(/\+/g, ' ');
  let parts;

  if ((parts = s3Uri.match('^https://s3.amazonaws.com/([^/]+)/(.*)'))) {
    return [parts[1], parts[2]];
  }
  if ((parts = s3Uri.match('^https://(.+).s3.amazonaws.com/(.*)'))) {
    return [parts[1], parts[2]];
  }
  if ((parts = s3Uri.match('^s3://([^/]+)/(.*)'))) {
    return [parts[1], parts[2]];
  }
  if ((parts = s3Uri.match('^arn:aws:s3:::([^/]+)/(.*)'))) {
    return [parts[1], parts[2]];
  }

  return [null, null];
};

export const removeDataUrlPrefix = (url: string) =>
  url.replace(/^data:image\/(png|jpeg|jpg);base64,/, ''); //already a dataURL but needs to have the prefix removed

/**
 * Modify an "original image" url to a "generated product image" URL
 * If the original is a -thumbnail use 300w (consistent with historical use)
 * Otherwise, use a maximum size of 2400w
 * THIS IS A WORKAROUND - IT SHOULD ONLY BE USED FOR OUR OWN PRODUCT IMAGES
 * @param originalUrl
 * @param width the desired width to select, will get the next closest size
 */
export const imgUrl = (originalUrl: string | null, width = 300): string => {
  if (originalUrl === null) {
    return '';
  } // this is no good
  // strip off https;?//s3.amazonaws.com
  // https://s3.amazonaws.com/sage.assets.inscyth/lifestyle/Granite+Lifestyle/M817L_lifestyle.jpeg
  // become /sage.assets.inscyth/lifestyle/Granite+Lifestyle/M817L_lifestyle.jpeg
  // or
  // $ md5 -s https://s3.amazonaws.com/sage.assets.inscyth/lifestyle/Granite+Lifestyle/M817L_lifestyle.jpeg
  // MD5 ("https://s3.amazonaws.com/sage.assets.inscyth/lifestyle/Granite+Lifestyle/M817L_lifestyle.jpeg") = dc8e1cb539ac6978875658e56a819004
  //  becomes /productimg/dc8e1cb539ac6978875658e56a819004/M817L_lifestyle.jpeg

  // cloudront for ee-web.dev.inscyth.com
  // sage.assets.inscyth -> origin is sage.assets.inscyth
  // hpro-countertops...
  // staging ones

  if (
    originalUrl[0] === '/' ||
    !originalUrl.match(/(hpro-countertops|sage)\.assets/gi)
  ) {
    return encodeURI(originalUrl);
  } // only mess with our known patterns

  const rewrite = localStorage.getItem('imgRewrite') || 'md5';

  // if you haven't set this value AND you're running a local instance, just use the base URL
  if ((!rewrite || rewrite === '') && document.URL.match(/localhost/)) {
    return encodeURI(originalUrl);
  }

  // strip off the domain completely to make relative references
  if (!rewrite || rewrite === '' || rewrite === 'prefix') {
    const baseUrl = originalUrl.replace('https://s3.amazonaws.com', '');

    return encodeURI(baseUrl);
  }

  // if you give a root domain, replace s3 with that domain
  if (rewrite.match('^http[s]*://')) {
    const baseUrl = originalUrl.replace('https://s3.amazonaws.com', rewrite);

    return encodeURI(baseUrl);
  }

  // if you want th md5, make it relative, prefix with /productimg/
  if (rewrite === 'md5') {
    const baseUrl = originalUrl.replace('-thumbnail', '').replace('+', ' ');
    const md5Hash = md5(baseUrl);

    const minSize = 300;
    const maxSize = 2400;
    const sizes = [minSize, 600, 1200, maxSize];
    // given a width, get a fixed value from the sizes array
    const adjWidth = width
      ? sizes.filter((v) => width <= v).shift() || maxSize
      : baseUrl === originalUrl
        ? maxSize
        : minSize;
    const size = adjWidth === 0 ? '' : `-${adjWidth}w`;

    const [, key] = bucketAndKeyFromS3URI(baseUrl);
    let baseName = key !== null ? key : '';

    if (baseName.lastIndexOf('/') >= 0) {
      baseName = baseName.substring(baseName.lastIndexOf('/') + 1);
    }
    let baseExt = '';

    if (baseName.lastIndexOf('.') >= 0) {
      baseExt = baseName.substring(baseName.lastIndexOf('.'));
      baseName = baseName.substring(0, baseName.lastIndexOf('.'));
    }

    const newUrl = `/productimg/${md5Hash}/${baseName}${size}${baseExt}`;

    return encodeURI(newUrl);
  }

  return encodeURI(originalUrl);
};
